import { FormikErrors, FormikHandlers, FormikHelpers, FormikState } from 'formik';
import { Box, BoxProps } from '@chakra-ui/react';
import { ISettingsValues } from 'types';
import { FormSwitch, SimpleGrid, Text, FormListSelection } from 'components/UI';
import ProductFieldTitle from 'components/common/ProductFieldTitle';
import { validateForm } from 'utils/helpers';
import SettingsFormControl from 'components/Settings/SettingsForm/SettingsFormControl';
import SettingsTextContainer from 'components/Settings/SettingsForm/SettingsTextContainer';

type RegionFormProps = {
  values: ISettingsValues | Record<string, never>;
  countries?: { name: string; iso2: string }[];
  submitted: boolean;
  errors: FormikErrors<ISettingsValues | Record<string, never>>;
  onBlur: FormikHandlers['handleBlur'];
  onChange: FormikHandlers['handleChange'];
  touched: FormikState<ISettingsValues | Record<string, never>>['touched'];
  setFieldValue: FormikHelpers<ISettingsValues | Record<string, never>>['setFieldValue'];
};

const GeneralSettingsFieldDescription = (props: BoxProps) => <Text as="small" w="full" {...props} />;

const RegionForm = ({ values, setFieldValue, onChange, onBlur, countries, ...props }: RegionFormProps) => {
  const formErrors = validateForm<ISettingsValues | Record<string, never>>(props);

  return (
    <Box display="flex" flexDir="column" h="full" gap={4}>
      <SettingsTextContainer>
        <ProductFieldTitle htmlFor="countries">COUNTRIES *</ProductFieldTitle>
        <GeneralSettingsFieldDescription>
          Countries to include in the registration and RFQ country options.
        </GeneralSettingsFieldDescription>
      </SettingsTextContainer>
      <SettingsFormControl
        justifyContent="flex-start"
        isInvalid={!!formErrors.countries}
        flex={1}
        mb={formErrors.countries ? 4 : 0}
      >
        <FormListSelection
          bgColor="white"
          _hover={{
            bgColor: 'white',
          }}
          _focus={{
            bgColor: 'white',
          }}
          containerProps={{
            flex: 1,
            h: 'full',
            __css: {
              'select option': {
                bgColor: 'white',
              },
            },
          }}
          resultContainerProps={{
            bg: 'white',
            flex: 1,
          }}
          name="countries"
          error={formErrors.countries}
          value={values.countries ?? []}
          onChange={onChange}
          onBlur={onBlur}
          options={countries?.map(({ name, iso2 }) => ({ label: name, value: iso2 })) ?? []}
          setFieldValue={setFieldValue}
        />
      </SettingsFormControl>

      <SimpleGrid columns={2}>
        <SettingsTextContainer>
          <ProductFieldTitle htmlFor="otherCountry">Other Country</ProductFieldTitle>
          <GeneralSettingsFieldDescription>
            Whether to include the &quot;Other&quot; country option in the list of countries.
          </GeneralSettingsFieldDescription>
        </SettingsTextContainer>
        <SettingsFormControl isInvalid={!!formErrors.otherCountry}>
          <FormSwitch
            name="otherCountry"
            value={values?.otherCountry ?? false}
            onChange={(e) => setFieldValue('otherCountry', e.target.checked)}
          />
        </SettingsFormControl>
      </SimpleGrid>
    </Box>
  );
};

export default RegionForm;
